/**
 * End-game summary.
 *
 * Ranking rule: cumulative total score is the primary determinant of rank.
 * Ties on total score are broken by round-win count (more wins ranks
 * higher). A further tie is broken by whoever's running total was already
 * at or above that final score earliest — the earlier round index ranks
 * higher. It's "at or above", not "first exact match": a player who peaked
 * higher and later gave points back should still be credited with having
 * reached that level first, even if the exact number only reappears on the
 * final round (e.g. one player sits at 500 by round 3, drops to 480 on
 * round 5; another climbs steadily and only reaches 480 on round 5 — the
 * first player already cleared 480 back at round 3 and wins the tiebreak).
 * This is about the SCORE, not the round-win count: in Serious mode a
 * player's running total moves on other players' rounds too (zero-sum
 * settlement), so it doesn't always land on the same round as "got that
 * many wins first." Players tied on every key still share a rank and are
 * reported as a genuine tie (only possible with 0 rounds won each, since a
 * round can only be won by one player).
 */

import type { PatternHit, PlayerId } from '../rules/types';
import type { Game, RoundRecord } from './types';

export interface PlayerSummary {
  player: PlayerId;
  name: string;
  /** Primary ranking key. */
  totalScore: number;
  /** First tiebreaker when totalScore is equal — more wins ranks higher. */
  roundsWon: number;
  /**
   * Round index at which this player's running total first reached (met or
   * exceeded) their final totalScore — 0 if it was already there before any
   * round. Second tiebreaker when totalScore and roundsWon are both equal —
   * the smaller (earlier) index ranks higher, i.e. whoever's score got at
   * least this good first, even if they later gave some of it back.
   */
  scoreReachedAtRound: number;
  /** Rounds where this player fed the winning tile. */
  timesDiscardedInto: number;
  selfDraws: number;
  /** 1-based. Players tied on totalScore, roundsWon, and scoreReachedAtRound share the same rank. */
  rank: number;
}

export interface GameSummary {
  gameId: string;
  tableId: string;
  roundsPlayed: number;
  players: PlayerSummary[];
  /** Everyone sharing rank 1. More than one entry means a genuine tie. */
  winners: PlayerId[];
  /** Every round, in order, for the round-by-round view. */
  rounds: RoundRecord[];
}

export function summarise(game: Game): GameSummary {
  const stats = new Map<PlayerId, Omit<PlayerSummary, 'rank' | 'scoreReachedAtRound'>>(
    game.players.map((p) => [
      p.id,
      {
        player: p.id,
        name: p.name,
        totalScore: 0,
        roundsWon: 0,
        timesDiscardedInto: 0,
        selfDraws: 0,
      },
    ]),
  );

  for (const round of game.rounds) {
    for (const payment of round.payments) {
      const s = stats.get(payment.player);
      if (s) s.totalScore += payment.delta;
    }
    if (round.outcome === 'win' && round.winner) {
      const w = stats.get(round.winner);
      if (w) {
        w.roundsWon += 1;
        if (round.selfDraw) w.selfDraws += 1;
      }
      if (round.discarder) {
        const d = stats.get(round.discarder);
        if (d) d.timesDiscardedInto += 1;
      }
    }
  }

  // Second pass: replay running totals to find, per player, the earliest
  // round index at which the running total was already at or above their
  // eventual final score (a peak they later gave back still counts).
  const scoreReachedAtRound = new Map<PlayerId, number>();
  const running = new Map<PlayerId, number>(game.players.map((p) => [p.id, 0]));
  for (const p of game.players) {
    if (0 >= (stats.get(p.id)?.totalScore ?? 0)) scoreReachedAtRound.set(p.id, 0);
  }
  for (const round of game.rounds) {
    for (const payment of round.payments) {
      running.set(payment.player, (running.get(payment.player) ?? 0) + payment.delta);
    }
    for (const p of game.players) {
      if (
        !scoreReachedAtRound.has(p.id) &&
        (running.get(p.id) ?? 0) >= (stats.get(p.id)?.totalScore ?? 0)
      ) {
        scoreReachedAtRound.set(p.id, round.index);
      }
    }
  }

  const full = [...stats.values()].map((s) => ({
    ...s,
    scoreReachedAtRound: scoreReachedAtRound.get(s.player) ?? 0,
  }));

  const ordered = full.sort((a, b) => {
    if (a.totalScore !== b.totalScore) return b.totalScore - a.totalScore;
    if (a.roundsWon !== b.roundsWon) return b.roundsWon - a.roundsWon;
    return a.scoreReachedAtRound - b.scoreReachedAtRound;
  });

  const ranked: PlayerSummary[] = [];
  let rank = 0;
  let lastKey: string | undefined;
  ordered.forEach((s, i) => {
    const key = `${s.totalScore}|${s.roundsWon}|${s.scoreReachedAtRound}`;
    if (key !== lastKey) {
      rank = i + 1;
      lastKey = key;
    }
    ranked.push({ ...s, rank });
  });

  return {
    gameId: game.id,
    tableId: game.tableId,
    roundsPlayed: game.rounds.length,
    players: ranked,
    winners: ranked.filter((p) => p.rank === 1).map((p) => p.player),
    rounds: game.rounds,
  };
}

export interface SessionBadge {
  label: string;
  detail: string;
}

/** "+1,024" — local to this file rather than importing `ui/helpers.ts`'s `formatDelta`, since `src/game/` stays UI-agnostic (see CLAUDE.md's architecture rules). */
function formatPoints(delta: number): string {
  const formatted = Math.abs(delta).toLocaleString('en-US');
  return delta < 0 ? `-${formatted}` : `+${formatted}`;
}

/** "2m 15s", "45s" — durations between rounds are always sub-day, so minutes:seconds is all this needs. */
export function formatDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
}

/** A winning round with the fields the records below rely on. */
type WinRound = RoundRecord & { winner: PlayerId };

/**
 * Who holds each of the game's lighthearted records — one round per record, or nothing when there is no
 * meaningful holder. The single source for both the Final Results badges and the per-player posters, so the two
 * can never disagree. Every value is read off `game.rounds` (no scoring); every tie is broken the same way, the
 * earliest round wins — a lighthearted record, not another ranking, so "first to get there" is a fine tiebreak.
 */
export interface SessionRecords {
  /** The winning round with the most fan (capped) — `Biggest Win`. */
  biggestWin?: WinRound & { effectiveFan: number };
  /** The winning round with the most distinct patterns stacked on one hand — `Most Combo Win`. */
  mostStacked?: WinRound;
  /** The single most valuable PATTERN anyone landed, and the round it landed in — `Star Pattern`. */
  star?: { round: WinRound; pattern: PatternHit };
  /** The winning rounds that took the least / the most wall-clock time. Only when at least two winning rounds were timed. */
  fastest?: { round: WinRound; durationMs: number };
  longest?: { round: WinRound; durationMs: number };
}

export function findSessionRecords(game: Game): SessionRecords {
  const records: SessionRecords = {};
  if (game.rounds.length === 0) return records;

  const winningRounds = game.rounds.filter(
    (r): r is WinRound & { effectiveFan: number } =>
      r.outcome === 'win' && r.winner !== undefined && r.effectiveFan !== undefined,
  );
  if (winningRounds.length > 0) {
    records.biggestWin = winningRounds.reduce((best, r) => (r.effectiveFan > best.effectiveFan ? r : best));
  }

  // A "combo" = each distinct pattern that fired on one hand (Seat Wind Pung,
  // Self-Draw, Flowers ×4, ... all landing on the same win) — a different
  // axis from Biggest Win's raw fan total, since a single high-value limit
  // hand (one pattern, capped fan) can easily out-fan a hand that stacked
  // many small bonuses without reaching the cap.
  const winningRoundsWithPatterns = game.rounds.filter(
    (r): r is WinRound => r.outcome === 'win' && r.winner !== undefined && r.patterns !== undefined,
  );
  if (winningRoundsWithPatterns.length > 0) {
    const mostStacked = winningRoundsWithPatterns.reduce((best, r) =>
      r.patterns!.length > best.patterns!.length ? r : best,
    );
    if (mostStacked.patterns!.length > 0) records.mostStacked = mostStacked;
  }

  // Star Pattern = the single most valuable PATTERN anyone landed on one
  // hand, as opposed to Biggest Win's round TOTAL — a player who stacked
  // five small bonuses can win Biggest Win without ever landing one
  // headline pattern, and this record is specifically about that headline
  // moment. Only each round's own highest-fan pattern is a candidate (a
  // player's other, smaller stacks on the same hand don't count), then the
  // best candidate wins across the whole game.
  let starRound: WinRound | undefined;
  let starPattern: PatternHit | undefined;
  for (const r of winningRoundsWithPatterns) {
    for (const p of r.patterns!) {
      if (!starPattern || p.fan > starPattern.fan) {
        starRound = r;
        starPattern = p;
      }
    }
  }
  if (starRound && starPattern) records.star = { round: starRound, pattern: starPattern };

  // Duration = wall-clock time between this round's recordedAt and the
  // previous round's (or game.startedAt for the first round) — the only
  // notion of "how long a round took" this data model has. Draws count for
  // timing (they still take real time) but only a win can be "won", so only
  // win rounds are eligible to actually BE the fastest/longest record.
  let previousRecordedAt = game.startedAt;
  const timedWinRounds: { round: WinRound; durationMs: number }[] = [];
  for (const r of game.rounds) {
    const durationMs = r.recordedAt - previousRecordedAt;
    if (r.outcome === 'win' && r.winner !== undefined) timedWinRounds.push({ round: r as WinRound, durationMs });
    previousRecordedAt = r.recordedAt;
  }
  // Fewer than 2 timed win rounds means "fastest" and "longest" would be the
  // same round, or there's nothing to compare at all — not a real superlative.
  if (timedWinRounds.length >= 2) {
    records.fastest = timedWinRounds.reduce((best, t) => (t.durationMs < best.durationMs ? t : best));
    records.longest = timedWinRounds.reduce((best, t) => (t.durationMs > best.durationMs ? t : best));
  }
  return records;
}

/**
 * A few lighthearted superlatives for the Final Results screen — every value
 * here is read straight off `summarise()`'s own output or `findSessionRecords`, never a new scoring
 * computation, so this can never disagree with the ranking table above it. Deliberately doesn't repeat anything
 * that table already shows a column for (rounds won, self-draws, fed a
 * win) — these are the extra story the table has no room for: which single
 * round was the big one, and how it played out. A category is omitted
 * entirely when it has no meaningful winner, down to no badges at all for a
 * 0-round game.
 */
export function computeSessionBadges(game: Game, summary: GameSummary): SessionBadge[] {
  const badges: SessionBadge[] = [];
  const records = findSessionRecords(game);
  const nameOf = (id: PlayerId | undefined) => summary.players.find((p) => p.player === id)?.name ?? '?';

  const { biggestWin, mostStacked, star, fastest, longest } = records;
  if (biggestWin) {
    badges.push({
      label: 'Biggest Win',
      detail: `${nameOf(biggestWin.winner)} — ${biggestWin.effectiveFan} fan${biggestWin.isLimit ? ' (limit)' : ''} (round #${biggestWin.index})`,
    });
  }
  if (mostStacked) {
    const points = mostStacked.payments.find((p) => p.player === mostStacked.winner)?.delta ?? 0;
    badges.push({
      label: 'Most Combo Win',
      detail: `${nameOf(mostStacked.winner)} — ${mostStacked.patterns!.length} combo${mostStacked.patterns!.length === 1 ? '' : 's'}, ${formatPoints(points)} points (round #${mostStacked.index})`,
    });
  }
  if (star) {
    badges.push({
      label: 'Star Pattern',
      detail: `${nameOf(star.round.winner)} — ${star.pattern.label} (${star.pattern.fan} fan${star.pattern.limit ? ', limit' : ''}) (round #${star.round.index})`,
    });
  }
  if (fastest && longest) {
    badges.push({
      label: 'Fastest Round',
      detail: `${nameOf(fastest.round.winner)} — ${formatDuration(fastest.durationMs)} (round #${fastest.round.index})`,
    });
    badges.push({
      label: 'Longest Round',
      detail: `${nameOf(longest.round.winner)} — ${formatDuration(longest.durationMs)} (round #${longest.round.index})`,
    });
  }
  return badges;
}

/** Running totals after each round — for a score-progression chart. */
export function scoreProgression(game: Game): Array<Record<PlayerId, number>> {
  const running: Record<PlayerId, number> = {};
  for (const p of game.players) running[p.id] = 0;

  return game.rounds.map((round) => {
    for (const payment of round.payments) {
      running[payment.player] = (running[payment.player] ?? 0) + payment.delta;
    }
    return { ...running };
  });
}
